import { db } from "@/utils/firebase";
import { collection, getDocs, query } from "firebase/firestore";

/*
 * Performance Indicators for Admin Dashboard:
 *
 * Number of:
 * New Sellers
 * New Buyers
 * New Sell Offers
 * New Buyers Offers
 */

const requestMetrics = async () => {
  try {
    // Fetch buyer and seller requests in parallel
    const [buyerRequestSnapshot, sellerRequestSnapshot] = await Promise.all([
      getDocs(collection(db, "buyer_requests")),
      getDocs(collection(db, "seller_requests")),
    ]);

    // Helper function to get top 5 Grades
    const getTopGradeFormCombos = (snapshot) => {
      const combosCount = {};

      // Collect grade + form combinations from the nested `product` field
      snapshot.docs.forEach((doc) => {
        const product = doc.data().product; // Access the product field
        if (product) {
          const { grade, form } = product; // Extract grade and form from product
          if (grade && form) {
            const comboKey = `${grade}|${form}`; // Create a unique key for the combo
            combosCount[comboKey] = (combosCount[comboKey] || 0) + 1;
          }
        }
      });

      // Sort by count in descending order and pick the top 5
      return Object.entries(combosCount)
        .sort(([, countA], [, countB]) => countB - countA)
        .slice(0, 5)
        .map(([combo, count]) => {
          const [grade, form] = combo.split("|");
          return { grade, form, combo, count };
        });
    };

    // Get top 5 grade + form combos for both buyer and seller requests
    const topBuyerCombos = getTopGradeFormCombos(buyerRequestSnapshot);
    const topSellerCombos = getTopGradeFormCombos(sellerRequestSnapshot);

    // Count of requests
    const buyerRequestCount = buyerRequestSnapshot.size;
    const sellerRequestCount = sellerRequestSnapshot.size;

    return {
      buyerRequestCount,
      sellerRequestCount,
      topBuyerCombos,
      topSellerCombos,
    };
  } catch (error) {
    console.error("Error fetching request metrics:", error);
    throw new Error("Failed to fetch request metrics");
  }
};

const userMetrics = async () => {
  try {
    // Fetch all user
    const rolesQuery = query(collection(db, "users"));
    const snapshot = await getDocs(rolesQuery);

    // Initialize counts
    let adminCount = 0;
    let buyerCount = 0;
    let sellerCount = 0;
    let buyerAgentCount = 0;
    let sellerAgentCount = 0;

    // Helper function to find top agents
    const getTopAgents = (snapshot) => {
      const agentCounts = {};

      // Create a map of user IDs to names for quick lookup
      const userMap = {};
      snapshot.docs.forEach((doc) => {
        const userData = doc.data();
        userMap[doc.id] = {
          email: userData.email,
          name: `${userData.first_name} ${userData.last_name}`,
        };
      });

      // Count agent occurrences
      snapshot.docs.forEach((doc) => {
        const agentId = doc.data().agent; // Assuming 'agent' field stores the agent ID
        if (agentId && userMap[agentId]) {
          // Check if agent ID exists and has a name
          agentCounts[agentId] = (agentCounts[agentId] || 0) + 1; // Count agent occurrences
        }
      });

      // Sort agents by count in descending order and pick the top 5
      return Object.entries(agentCounts)
        .sort(([, countA], [, countB]) => countB - countA)
        .slice(0, 5)
        .map(([agentId, count]) => ({
          agent_id: agentId, // Return agent_id
          name: userMap[agentId].name, // Get name from the userMap
          email: userMap[agentId].email, // Get name from the userMap
          count,
        }));
    };
    // Process results
    snapshot.docs.forEach((doc) => {
      const roles = doc.data().roles || [];
      if (roles.includes("admin")) adminCount++;
      if (roles.includes("buyer")) buyerCount++;
      if (roles.includes("seller")) sellerCount++;
      if (roles.includes("buyer_agent")) buyerAgentCount++;
      if (roles.includes("seller_agent")) sellerAgentCount++;
    });

    // Get the total user count excluding admins
    let totalUserCount = snapshot.size - adminCount;

    // Get the top 5 most repeated agents
    const topAgents = getTopAgents(snapshot);

    return {
      buyerCount,
      sellerCount,
      buyerAgentCount,
      sellerAgentCount,
      totalUserCount,
      topAgents,
    };
  } catch (error) {
    console.error("Error fetching all requests:", error);
    throw new Error("Failed to fetch all requests");
  }
};

const getMetrics = async () => {
  const userData = await userMetrics();
  const requestData = await requestMetrics();

  return {
    ...userData,
    ...requestData,
  };
};

export default getMetrics;
